import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthActionHandler from '../routes/AuthRoutes';

// Lazy load components
const LoginPage = React.lazy(() => import('../LoginPage'));

// Loading component for unauthenticated routes
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-secondary">
    <div className="flex flex-col items-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      <span className="mt-4 text-text-secondary">Loading...</span>
    </div>
  </div>
);

/**
 * UnauthenticatedApp - Wrapper component for unauthenticated user flows
 * 
 * This component handles all routes and functionality for users who are not authenticated.
 * It provides a clean separation between authenticated and unauthenticated flows.
 * 
 * Features:
 * - Login page routing
 * - Authentication action handling (password reset, email verification)
 * - Clean separation from authenticated app functionality
 * - Consistent loading states for unauthenticated routes
 * 
 * Requirements addressed:
 * - 2.1: Smooth login process with loading states
 * - 2.2: Responsive login experience
 * - 4.2: Clean separation between authenticated and unauthenticated flows
 */
const UnauthenticatedApp = () => {
  const { authError } = useAuth();

  return (
    <div className="min-h-screen w-full bg-background text-text-primary">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Main login route */}
          <Route path="/login" element={<LoginPage authError={authError} />} />

          {/* Authentication action handlers */}
          <Route path="/auth-action-handler" element={<AuthActionHandler />} />
          <Route path="/auth/verify-email" element={<Navigate to="/auth-action-handler?mode=verifyEmail" replace />} />
          <Route path="/auth/reset-password" element={<Navigate to="/auth-action-handler?mode=resetPassword" replace />} />
          <Route path="/auth/confirm-email-change" element={<Navigate to="/auth-action-handler?mode=confirmEmailChange" replace />} />

          {/* Firebase default auth action URLs */}
          <Route path="/__/auth/action" element={<AuthActionHandler />} />

          {/* Default redirect to login for any other route */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
};

export default UnauthenticatedApp;